import { Heart } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';

import { getBackgroundColorStyle } from '../../utils/background';
import { computeColor } from '../../utils/colors';
import { computeFont } from '../../utils/fonts';
import { getFontSizeVariables } from '../../utils/getFontSizeVariables';

import type { NavbarMenuDropdownItemElementProps } from '../../navbarElement/Navbar.type';
import { renderItemIcon } from '../../navbarElement/renderItemIcon';
import * as styles from '../Navbar.css';
import { NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2 } from './NavbarMenuDropdownItem.constants';

export function NavbarMenuDropdownItemV2(props: NavbarMenuDropdownItemElementProps & { isActive?: boolean }) {
  const { element, className, onClick, attributes, children, isActive } = props;
  const {
    style,
    padding,
    background,
    borderRadius,
    customAttributes,
    titleFontSize,
    titleFontWeight,
    titleLetterSpacing,
    descriptionFontSize,
    descriptionFontWeight,
    descriptionLetterSpacing,
    icon,
    iconColor,
    iconBackgroundColor,
    href,
    new_tab,
    color,
    fontFamily,
    descriptionFontFamily,
    descriptionColor,

    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverDescriptionTextColor,
    hoverIconColor,
    hoverIconBackgroundColor,
    hoverBackgroundColor,
    hoverUnderline,

    activeTextColor,
    activeUnderline,
    activeFontWeight,

    // Mobile attributes
    mobileBackground,
    mobilePadding,
    mobileBorderRadius,
    mobileBorderColor,
    mobileBorderStyle,
    mobileBorderWidth,
    mobileTitleFontSize,
    mobileTitleColor,
    mobileDescriptionFontSize,
    mobileDescriptionColor,
  } = element.attrs || {};

  const visible = element.attrs?.visible ?? true;

  if (!visible) {
    return null;
  }

  const isInEditor = attributes?.['data-is-in-editor'] === 'true';

  return (
    <a
      {...props}
      {...attributes}
      {...customAttributes}
      className={classNames(
        styles.navbarDropdownItemBaseV2,
        style === 'featured' ? styles.navbarDropdownItemFeaturedV2 : styles.navbarDropdownItemV2,
        className,
        customAttributes?.className,
      )}
      onClick={(e) => {
        if (onClick) {
          e?.preventDefault();
          onClick();
        }
      }}
      style={
        {
          '--padding': padding ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.padding,
          '--border-radius': borderRadius ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderRadius,
          '--background-color': getBackgroundColorStyle(background ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.background)
            .background,
          '--text-color': computeColor(color ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.textColor),
          '--icon-color': computeColor(iconColor ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.iconColor),
          '--icon-background-color': computeColor(
            iconBackgroundColor ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.iconBackgroundColor,
          ),
          ...(fontFamily ? { '--font-family': computeFont(fontFamily) } : {}),
          ...getFontSizeVariables(
            titleFontSize ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.titleFontSize,
            mobileTitleFontSize,
            'title',
          ),
          '--title-font-weight': titleFontWeight ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.titleFontWeight,
          ...getFontSizeVariables(
            descriptionFontSize ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionFontSize,
            mobileDescriptionFontSize,
            'description',
          ),
          '--title-letter-spacing': titleLetterSpacing ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.titleLetterSpacing,
          '--description-letter-spacing':
            descriptionLetterSpacing ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionLetterSpacing,
          '--description-font-weight':
            descriptionFontWeight ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionFontWeight,
          '--description-text-color': computeColor(
            descriptionColor ?? NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionTextColor,
          ),
          ...(descriptionFontFamily ? { '--description-font-family': computeFont(descriptionFontFamily) } : {}),

          '--hover-border-radius':
            hoverBorderRadius || borderRadius || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderRadius,
          '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.textColor,
          '--hover-description-text-color':
            hoverDescriptionTextColor || descriptionColor || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionTextColor,
          '--hover-background-color':
            hoverBackgroundColor || background || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.hoverBackgroundColor,
          '--hover-icon-color': hoverIconColor || iconColor || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.iconColor,
          '--hover-icon-background-color':
            hoverIconBackgroundColor ||
            iconBackgroundColor ||
            NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.iconBackgroundColor,
          '--hover-border-color': hoverBorderColor || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderColor,
          '--hover-border-style': hoverBorderStyle || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderStyle,
          '--hover-border-width': hoverBorderWidth || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderWidth,
          '--hover-underline': hoverUnderline ? 'underline' : 'none',

          '--active-text-color': activeTextColor ? computeColor(activeTextColor) : undefined,
          '--active-underline': activeUnderline ? 'underline' : undefined,
          '--active-font-weight': activeFontWeight,

          // Mobile CSS variables
          '--mobile-background-color': getBackgroundColorStyle(
            mobileBackground || background || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.background,
          ).background,
          '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.padding,
          '--mobile-border-radius':
            mobileBorderRadius || borderRadius || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderRadius,
          '--mobile-border-color': mobileBorderColor || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderColor,
          '--mobile-border-style': mobileBorderStyle || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderStyle,
          '--mobile-border-width': mobileBorderWidth || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.borderWidth,
          '--mobile-title-color': computeColor(
            mobileTitleColor || color || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.textColor,
          ),
          '--mobile-description-color': computeColor(
            mobileDescriptionColor || descriptionColor || NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS_V2.descriptionTextColor,
          ),
        } as CSSProperties
      }
      href={href}
      target={new_tab ? '_blank' : '_self'}
      data-active={isActive || undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      {['icon', 'featured'].includes(style || '') && (
        <div className={styles.navbarDropdownIconV2}>
          {icon ? (
            renderItemIcon({ icon, color: iconColor })
          ) : (
            <Heart size={24} weight="fill" className="text-wb-secondary" />
          )}
        </div>
      )}
      <div className={styles.navbarDropdownItemTextsV2}>
        {/* An assumption is made here that the children of this element are always
            going to be a title and a description
            In future if we want to expand this to have richer content, this should
            not be static, but we should break add a title and description component
            and add cases in the serializer
        */}
        {isInEditor || !Array.isArray(children) ? (
          children
        ) : (
          <>
            {children[0] && <p className={styles.navbarDropdownItemTitleV2}>{children[0]}</p>}
            {children[1] && ['description', 'icon', 'featured'].includes(style || '') && (
              <p className={styles.navbarDropdownItemDescriptionV2}>{children[1]}</p>
            )}
          </>
        )}
      </div>
    </a>
  );
}
